import React, {
  FC, ReactNode, useCallback, useEffect, useLayoutEffect, useReducer, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useOrganizationState } from '@/state';
import { MenuItemProps } from '@/types';

import {
  DockedHelpAiChat,
  HelpAiChatProvider,
  useHelpAiChat,
} from '../../views/Dashboard/components/HelpAiChat';
import { SessionIdleGuard } from '../SessionIdleGuard';
import { StripeTermsGate } from '../StripeTermsGate';
import {
  LayoutWrapper,
  Main,
  MainHeader,
  MobileFooter,
  PageHeadline,
} from './Layout.styled';
import { MobileTables } from './MobileTables';
import { MobileTrialBar } from './MobileTrialBar';
import { RouteProgress } from './RouteProgress';
import { Sidebar } from './Sidebar';
import { SidebarAutoCollapseContext } from './sidebarAutoCollapse';
import {
  initSidebarPinnedState,
  persistSidebarPinnedPreference,
  resolveSidebarPinned,
  sidebarPinnedReducer,
  toggledSidebarPinnedPreference,
} from './sidebarPinned';
import { TopBar } from './TopBar';

const MAIN_CONTENT_ID = 'main-content';
/** Numer wersji panelu z `package.json` (Vite `define`), jak w stopce sidebara. */
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

interface LayoutProps {
    children: ReactNode;
    menuConfig? : MenuItemProps[];
}
// Aktywną (wybraną) organizację ładuje ProtectedRoute (loader do orgResolved).
// Lista dostępnych org (getOrganizationsByIds) ładuje się W TLE — jej loading/błąd
// obsługuje OrganizationSelector (sidebar + modal); NIE blokuje dashboardu.
// Wewnętrzny layout — czyta stan asystenta AI (`useHelpAiChat`), więc musi żyć
// wewnątrz `HelpAiChatProvider`. Gdy dok czatu jest otwarty, wpychamy całą ramę
// aplikacji w lewo (`$chatOpen` → padding-right na desktopie), a sidebar zwija
// się samodzielnie (czyta ten sam kontekst).
const LayoutInner: FC<LayoutProps> = ({
  children,
  menuConfig,
}) => {
  const { t } = useTranslation();
  const [showMobileNavigation, setShowMobileNavigation] = useState(false);
  const location = useLocation();
  const { isOpen: isChatOpen } = useHelpAiChat();
  const { isDisabled } = useOrganizationState();

  // Przypięty sidebar (desktop): zostaje rozwinięty, a treść robi mu miejsce. Wybór pamiętamy
  // w przeglądarce, żeby przetrwał odświeżenie. Strona może poprosić o chwilowe zwinięcie menu
  // (czat AI obok formularza, useSidebarAutoCollapse); logika w sidebarPinned.ts.
  const [sidebarPinned, dispatchSidebarPinned] = useReducer(sidebarPinnedReducer, undefined, initSidebarPinnedState);
  const isSidebarPinnedEffective = resolveSidebarPinned(sidebarPinned);
  // Po commicie, nie w reducerze: nawigacja formularz -> formularz zwalnia i zgłasza prośbę w jednym commicie,
  // a ręczne przypięcie ma przetrwać do zamknięcia czatu (licznik 0 po commicie).
  useEffect(() => {
    if (sidebarPinned.collapseRequests === 0) dispatchSidebarPinned({ type: 'settleCollapse' });
  }, [sidebarPinned.collapseRequests]);
  const requestSidebarCollapse = useCallback((active: boolean): void => {
    dispatchSidebarPinned({ type: active ? 'requestCollapse' : 'releaseCollapse' });
  }, []);

  const toggleSidebarPinned = useCallback((): void => {
    // Zapis poza reducerem (musi zostać czysty, StrictMode woła go dwa razy); tylko pinezka
    // zmienia preferencję, chwilowe zwinięcie nie.
    persistSidebarPinnedPreference(toggledSidebarPinnedPreference(sidebarPinned));
    dispatchSidebarPinned({ type: 'togglePinned' });
  }, [sidebarPinned]);

  useLayoutEffect(() => {
    setShowMobileNavigation(false);
    // Reset scroll on window (mobile)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Reset scroll on Main container (desktop/tablet)
    const mainElement = document.getElementById(MAIN_CONTENT_ID);
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [location.pathname]);

  return (
    <SidebarAutoCollapseContext.Provider value={requestSidebarCollapse}>
      <LayoutWrapper $isMenuOpen={showMobileNavigation} $chatOpen={isChatOpen} $sidebarPinned={isSidebarPinnedEffective}>
        {/* Pasek postępu przejścia między widokami, nad topbarem. */}
        <RouteProgress />
        <Sidebar
          menuConfig={menuConfig}
          isPinned={isSidebarPinnedEffective}
          onTogglePinned={toggleSidebarPinned}
          showMobile={showMobileNavigation}
          setShowMobile={() => setShowMobileNavigation(!showMobileNavigation)}
        />
        <PageHeadline>
          <MainHeader>
            <TopBar showMobileNavigation={() => setShowMobileNavigation(!showMobileNavigation)} />
          </MainHeader>
        </PageHeadline>
        <Main id={MAIN_CONTENT_ID} $chatOpen={isChatOpen}>
          {children}
          {/* Telefon: stopka z wersją aplikacji na końcu treści (sidebar jest wtedy schowany). */}
          <MobileFooter>
            <span>{`© ${new Date().getFullYear()} HotelSpot`}</span>
            <span>{t('sidebar.version', 'Wersja {{version}}', { version: APP_VERSION })}</span>
          </MobileFooter>
        </Main>
        {/* Telefon: tabele w treści jako lista kart, bez przewijania w bok. */}
        <MobileTables containerId={MAIN_CONTENT_ID} />
        <MobileTrialBar />
        {/* Czat AI nie ma sensu przy wyłączonej organizacji — panel i tak
          nie pokazuje treści, których asystent miałby dotyczyć. */}
        {!isDisabled && <DockedHelpAiChat />}
        <SessionIdleGuard />
        {/* Nowa wersja regulaminu płatności — prośba o akceptację zaraz po zalogowaniu.
          Komponent sam decyduje, czy się pokazać (tylko przy zmianie wersji). */}
        <StripeTermsGate />
      </LayoutWrapper>
    </SidebarAutoCollapseContext.Provider>
  );
};

export const Layout: FC<LayoutProps> = (props) => (
  <HelpAiChatProvider>
    <LayoutInner {...props} />
  </HelpAiChatProvider>
);
